import CustomHTMLElement from '@onpace/onspace-core/components/html_element'
import { OnspaceDialogSelect } from '@onpace/onspace-core/elements/dialog'

import { registerFormSubmissionCallback, registerFormCompletionCallback } from '@onpace/onspace-core/components/form'

////////// Forms

registerFormSubmissionCallback((form) => {
  form.querySelectorAll('input-array').forEach((element) => element.disableSelectionInputs())
})

registerFormCompletionCallback((form) => {
  form.querySelectorAll('input-array').forEach((element) => element.enableSelectionInputs())
})

////////// Elements

/// An element which allows inputs to be reused with an array of values.
///
/// This works by allowing elements to be added, removed and rearranged on the page. When adding a new element, a new
/// InputArrayControl is created, with it's contents coming from the template, when removing an element, the control is
/// removed from the dom, and when rearranging, the items are simply reordered in the DOM.
///
/// You must set the +name+ attribute on this class to the parameter name in the form, and the following children at
/// root level:
/// - An element with the attribute +data-input-array-template+.
///
/// In order to add controls, you can optionally provide an anchor element at root level. It will be subscribed to for
/// click events which will add a new control.
export default class InputArray extends CustomHTMLElement {
  /// Sets up the input array element.
  ///
  /// Locates the required children and adds events where necessary.
  runConstructor() {
    super.runConstructor()

    if (this.closest('[data-item-template]')) { return }

    this.multiple = this.getBooleanAttribute('multiple')

    this.name = this.getAttribute('name')
    this.controlContentTemplate = this.querySelector('[data-input-array-template]')
    if (this.controlContentTemplate) {
      this.controlContentTemplate.remove()
    }

    this.addButton = this.querySelector(':scope > a')
    if (this.addButton) {
      this.addButton.addEventListener('click', this.addControl.bind(this))
    }

    this.selectElement = this.querySelector('[data-input-array-select]')
    if (this.selectElement) {
      this.selectElement.addEventListener('change', this.selectElementChanged.bind(this))
    }

    this.configureSelectionDialog()

    this.addEventListener('dragover', this.draggedOver.bind(this))
  }

  /// Detects whether this element is disabled, based on a parent fieldset.
  get disabled() {
    const fieldset = this.closest('fieldset')
    if (fieldset) {
      return fieldset.disabled
    } else {
      return false
    }
  }

  ////////// Controls

  /// Retrieves the control elements from the DOM.
  get controls() {
    return this.querySelectorAll('input-array-control')
  }

  /// Creates and inserts a control into the DOM.
  addControl(_event) {
    if (this.disabled) { return }

    const control = new InputArrayControl(this.controlContentTemplate)
    this.insertBefore(control, this.addButton)

    this.triggerEvent('input-array:add-control', control)
  }

  /// Responds to a control being removed.
  ///
  /// If required, this updates the select element.
  controlRemoved(control) {
    if (!this.selectElement) { return }

    const controlValue = control.querySelector('input[type=hidden]').value
    this.selectElement.options.forEach((option) => {
      if (option.input.value === controlValue) {
        option.input.checked = false
      }
    })
  }

  ////////// Ordering

  /// Initiates a drag.
  ///
  /// When a control begins a drag, it calls this function to notify this element and setup for the drag.
  beginDrag(control) {
    this.draggingControl = control
    this.dragControls = Array.from(this.controls)
    this.dragIndex = this.dragControls.indexOf(this.draggingControl)

    const offset = document.documentElement.scrollTop - document.documentElement.clientTop
    this.dragCenters = this.dragControls.map((i) => {
      const rect = i.getBoundingClientRect()
      return rect.top + (rect.height / 2) + offset
    })
  }

  /// Finishes a drag.
  ///
  /// When a control ends a drag, it calls this function to notify this element and cleanup from the drag.
  endDrag() {
    this.draggingControl = null
    this.dragControls = null
    this.dragCenters = null
    this.dragIndex = null
  }

  /// Updates from a current drag.
  ///
  /// This function is called during a drag, when the control is above this element. The position of the dragged control
  /// relative to the other controls is calculated, and its position in the DOM is changed if necessary.
  draggedOver(event) {
    if (!this.draggingControl) { return }
    event.preventDefault()

    let i = -1
    do {
      i += 1
    } while (i < this.dragControls.length && event.pageY > this.dragCenters[i])

    const insertIndex = i
    if (i > this.dragIndex) {
      i -= 1
    }

    if (this.dragIndex != i) {
      this.dragIndex = i
      this.insertBefore(this.draggingControl, this.children[insertIndex])

      this.triggerEvent('input-array:order')
    }
  }

  ////////// Selection

  /// Responds to changes in the internal select element.
  selectElementChanged(event) {
    if (event.target.checked) {
      const selectOption = event.target.closest('.select-custom__option')
      this.addSelection({
        value: event.target.value,
        title: selectOption.title,
        subtitle: selectOption.subtitle
      })
    } else {
      this.removeSelection(event.target.value)
    }
  }

  /// Adds a selection with the given data.
  addSelection(option) {
    if (!this.multiple) {
      this.controls.forEach((control) => control.remove())
    }

    const control = new InputArrayControl(this.controlContentTemplate)
    this.insertBefore(control, this.lastChild)

    const controlInput = control.querySelector('input[type=hidden]')
    controlInput.value = option.value

    const controlTitle = control.querySelector('.input-array__selection__title')
    controlTitle.innerText = option.title

    const controlSubtitle = control.querySelector('.input-array__selection__subtitle')
    if (controlSubtitle) {
      controlSubtitle.innerText = option.subtitle
    }

    const controlImage = control.querySelector('.input-array__selection__image')
    if (controlImage) {
      controlImage.style.backgroundImage = `url(${option.imageUrl})`
    }

    this.triggerEvent('input-array:add-control', control)
  }

  /// Removes a selection with the given value.
  removeSelection(value) {
    this.controls.forEach((control) => {
      const controlValue = control.querySelector('input[type=hidden]').value
      if (controlValue === value) {
        control.removeControl()
      }
    })
  }

  /// Disables all inputs within the selection element, if it exists.
  disableSelectionInputs() {
    if (this.selectElement) {
      this.selectElement.querySelectorAll('input').forEach((element) => element.disabled = true)
    }
  }

  /// Enables all inputs within the selection element, if it exists.
  enableSelectionInputs() {
    if (this.selectElement) {
      this.selectElement.querySelectorAll('input').forEach((element) => element.disabled = false)
    }
  }

  ///// Select Dialog

  /// Detects and configures elements used to add items via dialogs.
  configureSelectionDialog() {
    const selectButton = this.querySelector('[data-array-select-dialog]')
    if (!selectButton) { return }

    const selectUrl = selectButton.getAttribute('data-array-select-dialog')
    selectButton.addEventListener('click', (_event) => this.presentSelectionDialog(selectUrl))

    const createButtons = this.querySelectorAll('[data-array-create-dialog]')
    createButtons.forEach((button) => {
      const createUrl = button.getAttribute('data-array-create-dialog')
      button.addEventListener('click', (_event) => {
        const dropdown = button.closest('drop-down')
        if (dropdown) { dropdown.hideMenu() }

        this.presentSelectionDialog(createUrl)
      })
    })
  }

  /// Creates and presents a new selection dialog instance.
  presentSelectionDialog(url) {
    const selectedValues = Array.from(this.controls).map((control) => control.querySelector('input[type=hidden]').value)
    this.selectionDialog = new OnspaceDialogSelect({ selectedValues: selectedValues })
    this.selectionDialog.addEventListener('onspace:dialog:close', this.dialogClosed.bind(this))
    this.selectionDialog.addEventListener('onspace:dialog:selected', this.selectionDialogValueSelected.bind(this))
    this.selectionDialog.addEventListener('onspace:dialog:deselected', this.selectionDialogValueDeselected.bind(this))
    this.selectionDialog.loadUrl(url)

    document.body.appendChild(this.selectionDialog)
  }

  /// Responds to close events on the selection dialog.
  dialogClosed(_event) {
    this.selectionDialog = null
  }

  /// Responds to selection events on the selection dialog.
  selectionDialogValueSelected(event) {
    this.addSelection(event.detail)
  }

  /// Responds to deselection events on the selection dialog.
  selectionDialogValueDeselected(event) {
    this.removeSelection(event.detail.value)
  }
}

/// An element which operates a single control within an InputArray.
///
/// This is responsible for the control-specific functionality of it's parent element. It does not require any children,
/// however you can include the following:
/// - An anchor with the attribute +data-input-array-drag+, which is required for reordering.
/// - An anchor with the attribute +data-input-array-remove+, which is required for removal.
export class InputArrayControl extends CustomHTMLElement {
  /// Sets up the control element.
  ///
  /// Locates the children and adds events where necessary.
  runConstructor(template) {
    super.runConstructor()

    if (template) {
      const index = Math.floor(Math.random() * 1000)
      this.innerHTML = template.innerHTML.replace(/\[\$idx\]/g, `[${index}]`)
    }

    this.dragButton = this.querySelector('[data-input-array-drag]')
    if (this.dragButton) {
      this.draggable = true
      this.addEventListener('dragstart', this.dragStarted.bind(this))
      this.addEventListener('dragend', this.dragEnded.bind(this))
    }

    this.removeButton = this.querySelector('[data-input-array-remove]')
    if (this.removeButton) {
      this.removeButton.addEventListener('click', this.removeControl.bind(this))
    }
  }

  /// Detects whether this element is disabled, based on a parent fieldset.
  get disabled() {
    const fieldset = this.closest('fieldset')
    if (fieldset) {
      return fieldset.disabled
    } else {
      return false
    }
  }

  ////////// Array

  /// Retrieves the parent InputArray element.
  get inputArray() {
    return this.parentElement
  }

  /// Removes this control from the DOM.
  removeControl(_event) {
    if (this.disabled) { return }

    this.inputArray.controlRemoved(this)
    this.triggerEvent('input-array:remove-control', this.inputArray)
    this.remove()
  }

  ////////// Ordering

  /// Initiates a drag.
  ///
  /// This is called when a user picks up the drag button. It notifies the parent InputArray and prepares itself for a
  /// drag.
  dragStarted(event) {
    if (this.disabled) {
      event.preventDefault()
      return
    }

    const eventElement = document.elementFromPoint(event.clientX, event.clientY)
    if (!eventElement || !eventElement.closest('[data-input-array-drag]')) { return event.preventDefault() }

    this.inputArray.beginDrag(this)

    setTimeout(() => this.style.opacity = 0)
  }

  /// Finishes a drag.
  ///
  /// This is called when a user drops the dragged element. It notifies the parent InputArray and cleans itself up from
  /// the drag.
  dragEnded(_event) {
    if (this.disabled) { return }

    this.inputArray.endDrag()

    this.style.opacity = ''
  }
}

window.customElements.define('input-array', InputArray)
window.customElements.define('input-array-control', InputArrayControl)
